import React, { useMemo } from "react";
import { Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend } from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

type ChartLineMatchPerformanceProps = {
  matches: PlayerMatch[],
  computeMatchRate: (match: PlayerMatch, overallVsGdRates: any, isHome: boolean) => number,
  overallVsGdRates: any,
  clubs: { id: number }[],
};

const ChartLineMatchPerformance: React.FC<ChartLineMatchPerformanceProps> = ({
  matches,
  computeMatchRate,
  overallVsGdRates,
  clubs,
}) => {
  const rootStyles = getComputedStyle(document.documentElement);
  const primaryColorRgb = rootStyles.getPropertyValue("--primary-color-rgb").trim();

  const data = useMemo(() => {
    if (!matches || matches.length === 0) return null;

    const sortedMatches = [...matches].sort(
      (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );

    const matchRates = sortedMatches.map((m) =>
      computeMatchRate(m, overallVsGdRates, clubs.map((c) => c.id).includes(m.homeClub.id))
    );

    const avgRates = matchRates.map((_, i) => {
      const lastFive = matchRates.slice(Math.max(0, i - 4), i + 1);
      const valid = lastFive.filter((r) => typeof r === "number");
      if (!valid.length) return null;
      const avg = valid.reduce((acc, r) => acc + r, 0) / valid.length;
      return Math.round(avg * 10) / 10;
    });

    return {
      labels: matches.map((m) => new Date(m.startDate).toLocaleDateString()),
      datasets: [
        {
          label: "Match Rate",
          data: matchRates,
          borderColor: `rgba(${primaryColorRgb},1)`,
          backgroundColor: `rgba(${primaryColorRgb},1)`,
          tension: 0.3,
          pointRadius: 2,
        },
        {
          label: "Average Rate (Last 5 matches)",
          data: avgRates,
          borderColor: `rgba(${primaryColorRgb},.5)`,
          backgroundColor: `rgba(${primaryColorRgb},.5)`,
          tension: 0.3,
          pointRadius: 2,
        },
      ],
    };
  }, [matches, computeMatchRate, overallVsGdRates, clubs]);

  if (!data) return <p>No matches available</p>;

  const pointLabelPlugin = {
    id: "pointLabelPlugin",
    afterDatasetsDraw(chart: any) {
      const { ctx } = chart;

      chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
        // ⭐ Only draw for the first dataset
        if (datasetIndex !== 0) return;

        const meta = chart.getDatasetMeta(datasetIndex);

        meta.data.forEach((point: any, index: number) => {
          const value = dataset.data[index];
          if (value == null) return;

          ctx.save();
          ctx.font = "10px Saira"; // small label
          ctx.fillStyle = dataset.borderColor;
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";

          ctx.fillText(value, point.x, point.y - 5); // move above the dot
          ctx.restore();
        });
      });
    },
  };

  return (
    <div className="h-100 w-100">
      <Line
        data={data}
        plugins={[pointLabelPlugin]}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            datalabels: { display: false },
          },
          layout: {
            padding: 0,
          },
          scales: {
            x: {
              display: false,
              offset: false,
              grid: { drawTicks: false, drawBorder: false, drawOnChartArea: false },
              ticks: { padding: 0 },
            },
            y: {
              display: false,
              min: 0,
              max: 10, // optional: set range if rates are 0-100
              grid: { drawTicks: false, drawBorder: false, drawOnChartArea: false },
              ticks: { padding: 0 },
            },
          },
        }}
      />
    </div>
  );
};

export default ChartLineMatchPerformance;
